import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useSortableTable, SortIndicator } from "@/hooks/useSortableTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Trash, Plus, Coffee } from "@phosphor-icons/react";
import { toast } from "sonner";

const KIND_LABELS = {
    comida: "Comida",
    descanso: "Descanso",
    personal: "Asunto personal",
};

export default function Breaks() {
    const today = new Date().toISOString().slice(0, 10);
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({
        entry_date: today,
        start_time: "13:30",
        end_time: "14:30",
        kind: "comida",
        note: "",
    });

    const load = async () => {
        const res = await http.get("/breaks");
        setItems(res.data);
    };
    useEffect(() => {
        load();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await http.post("/breaks", form);
            toast.success("Pausa registrada");
            setForm({ ...form, note: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al registrar pausa");
        }
    };

    const remove = async (id) => {
        await http.delete(`/breaks/${id}`);
        load();
    };

    const totalToday = items
        .filter((i) => i.entry_date === today)
        .reduce((s, i) => s + i.minutes, 0);

    const accessors = useMemo(
        () => ({
            entry_date: (b) => b.entry_date || "",
            kind: (b) => KIND_LABELS[b.kind] || b.kind,
            horario: (b) => b.start_time || "",
            minutes: (b) => Number(b.minutes || 0),
        }),
        [],
    );
    const { sorted, sortKey, sortDir, headerProps } = useSortableTable(
        items,
        "breaks-list",
        accessors,
        { initialKey: "entry_date", initialDir: "desc" },
    );

    return (
        <div data-testid="page-breaks">
            <PageHeader
                eyebrow="Registro de jornada"
                title="Pausas y descansos"
                description="Cumple con el registro de jornada anotando inicio y fin de cada pausa diaria."
            />
            <div className="p-6 lg:p-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <form
                    onSubmit={submit}
                    className="border border-slate-200 bg-white p-6 lg:col-span-1"
                    data-testid="break-form"
                >
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">
                        Nueva pausa
                    </div>
                    <Label className="text-xs">Fecha</Label>
                    <Input
                        type="date"
                        value={form.entry_date}
                        onChange={(e) =>
                            setForm({ ...form, entry_date: e.target.value })
                        }
                        className="mt-1 rounded-sm border-slate-300"
                        data-testid="break-date"
                    />
                    <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                            <Label className="text-xs">Inicio</Label>
                            <Input
                                type="time"
                                value={form.start_time}
                                onChange={(e) =>
                                    setForm({ ...form, start_time: e.target.value })
                                }
                                className="mt-1 rounded-sm border-slate-300 font-mono"
                                data-testid="break-start"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Fin</Label>
                            <Input
                                type="time"
                                value={form.end_time}
                                onChange={(e) =>
                                    setForm({ ...form, end_time: e.target.value })
                                }
                                className="mt-1 rounded-sm border-slate-300 font-mono"
                                data-testid="break-end"
                            />
                        </div>
                    </div>
                    <Label className="text-xs mt-3 block">Tipo</Label>
                    <Select
                        value={form.kind}
                        onValueChange={(v) => setForm({ ...form, kind: v })}
                    >
                        <SelectTrigger
                            className="mt-1 rounded-sm border-slate-300"
                            data-testid="break-kind"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="comida">Comida</SelectItem>
                            <SelectItem value="descanso">Descanso corto</SelectItem>
                            <SelectItem value="personal">Asunto personal</SelectItem>
                        </SelectContent>
                    </Select>
                    <Label className="text-xs mt-3 block">Nota (opcional)</Label>
                    <Input
                        value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        className="mt-1 rounded-sm border-slate-300"
                        placeholder="Ej: comida con cliente"
                        data-testid="break-note"
                    />
                    <Button
                        type="submit"
                        className="w-full mt-5 rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                        data-testid="break-submit"
                    >
                        <Plus size={14} className="mr-2" /> Registrar pausa
                    </Button>
                </form>

                <div className="lg:col-span-2">
                    <div className="border border-slate-200 bg-white">
                        <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    Hoy
                                </div>
                                <div className="font-display text-xl font-semibold mt-1">
                                    {Math.floor(totalToday / 60)}h {totalToday % 60}m de
                                    pausa
                                </div>
                            </div>
                            <Coffee size={28} className="text-slate-300" />
                        </div>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                    <th {...headerProps("entry_date", "py-3 px-4 text-left")}>
                                        Fecha
                                        <SortIndicator
                                            active={sortKey === "entry_date"}
                                            dir={sortDir}
                                        />
                                    </th>
                                    <th {...headerProps("kind", "py-3 px-4 text-left")}>
                                        Tipo
                                        <SortIndicator
                                            active={sortKey === "kind"}
                                            dir={sortDir}
                                        />
                                    </th>
                                    <th {...headerProps("horario", "py-3 px-4 text-left")}>
                                        Horario
                                        <SortIndicator
                                            active={sortKey === "horario"}
                                            dir={sortDir}
                                        />
                                    </th>
                                    <th {...headerProps("minutes", "py-3 px-4 text-right")}>
                                        Duración
                                        <SortIndicator
                                            active={sortKey === "minutes"}
                                            dir={sortDir}
                                        />
                                    </th>
                                    <th className="py-3 px-4"></th>
                                </tr>
                            </thead>
                            <tbody data-testid="breaks-list">
                                {items.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="text-center text-slate-500 text-sm py-12"
                                        >
                                            Aún no has registrado pausas.
                                        </td>
                                    </tr>
                                )}
                                {sorted.map((b) => (
                                    <tr
                                        key={b.break_id}
                                        className="border-b border-slate-100 hover:bg-slate-50"
                                    >
                                        <td className="py-3 px-4 font-mono text-slate-700">
                                            {b.entry_date}
                                        </td>
                                        <td className="py-3 px-4 text-slate-700">
                                            {KIND_LABELS[b.kind] || b.kind}
                                        </td>
                                        <td className="py-3 px-4 font-mono text-slate-700">
                                            {b.start_time} – {b.end_time}
                                        </td>
                                        <td className="py-3 px-4 text-right font-mono tabular-nums text-slate-900 font-semibold">
                                            {Math.floor(b.minutes / 60)}h {b.minutes % 60}m
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <button
                                                onClick={() => remove(b.break_id)}
                                                className="text-slate-400 hover:text-red-600"
                                                data-testid={`break-delete-${b.break_id}`}
                                            >
                                                <Trash size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
